/*
 * Created on Wed Nov 01 2023 4:27:51 PM
 *
 * ***************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2023
 *
 * All Rights Reserved
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ****************************************************************************************
 */
import React from "react";
import { ArrowRight, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getIcon } from "../utils/iconResolver";
import data from "../data/krumagoData.json";

const ParentIndustries = () => {
  const { industries } = data;
  const nav = useNavigate();

  return (
    <section
      id="parent-industries"
      className="relative py-16 sm:py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-blue-200/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 left-0 w-64 h-64 sm:w-96 sm:h-96 bg-purple-200/30 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-60 h-60 sm:w-80 sm:h-80 bg-cyan-200/20 rounded-full blur-3xl animate-pulse delay-500" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-12 sm:mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-5 sm:px-8 py-3 sm:py-4 rounded-2xl text-base sm:text-lg font-bold mb-6 sm:mb-8 shadow-2xl">
            <Globe className="w-5 h-5 sm:w-6 sm:h-6" />
            <span>{industries.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-slate-900 mb-6 sm:mb-8 tracking-tight leading-tight">
            {industries.headline.main}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              {industries.headline.accent}
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed font-light">
            {industries.description}
          </p>
        </div>

        {/* Industry Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-10 mb-12 sm:mb-16 lg:mb-20">
          {industries.items.map((industry, index) => (
            <div
              key={index}
              className="group relative cursor-pointer"
              onClick={() => nav(industry.href)}
            >
              <div
                className={`relative bg-gradient-to-br ${industry.bgGradient} border ${industry.borderColor} rounded-3xl p-8 sm:p-10 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 overflow-hidden h-full flex flex-col`}
              >
                {/* Gradient overlay on hover */}
                <div
                  className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                />

                {/* Icon */}
                <div
                  className={`w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${industry.gradient} rounded-3xl flex items-center justify-center text-white mb-6 sm:mb-8 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg flex-shrink-0`}
                >
                  {getIcon(industry.icon, { className: "w-8 h-8 sm:w-10 sm:h-10" })}
                </div>

                {/* Content */}
                <div className="flex-1">
                  <h3
                    className={`text-2xl sm:text-3xl font-black text-slate-900 mb-2 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r ${industry.gradient} transition-all duration-500`}
                  >
                    {industry.label}
                  </h3>
                  <p className={`text-xs sm:text-sm font-bold ${industry.accentColor} mb-3 sm:mb-4`}>
                    {industry.tagline}
                  </p>
                  <p className="text-slate-600 leading-relaxed text-sm sm:text-base font-light mb-6 sm:mb-8">
                    {industry.description}
                  </p>

                  {/* Highlights */}
                  <div className="flex flex-wrap gap-2 mb-6 sm:mb-8">
                    {industry.highlights.map((h, i) => (
                      <span
                        key={i}
                        className={`text-xs font-bold px-3 py-1.5 rounded-full bg-white/70 border ${industry.borderColor} text-slate-700`}
                      >
                        {h}
                      </span>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div
                  className={`inline-flex items-center gap-2 font-black ${industry.accentColor} group-hover:gap-3 transition-all duration-300 mt-auto text-sm sm:text-base`}
                >
                  <span>Explore {industry.label}</span>
                  <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </div>

                {/* Bottom accent */}
                <div
                  className={`absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r ${industry.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-b-3xl`}
                />
              </div>

              {/* Glow */}
              <div
                className={`absolute -inset-4 bg-gradient-to-r ${industry.gradient} rounded-3xl blur-2xl opacity-0 group-hover:opacity-15 transition-opacity duration-500 -z-10`}
              />
            </div>
          ))}
        </div>

        {/* More Industries Teaser */}
        <div className="text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-2 sm:gap-3 bg-white border border-slate-200 rounded-2xl px-5 sm:px-8 py-3 sm:py-4 shadow-lg">
            <span className="text-slate-600 font-medium text-sm sm:text-base">More industries coming:</span>
            {industries.comingTags.map((tag) => (
              <span
                key={tag}
                className="bg-slate-100 text-slate-500 text-xs sm:text-sm px-3 py-1 rounded-full font-semibold"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ParentIndustries;